import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {useSelector} from 'react-redux'
import {http} from 'hiya-common/src/utils/axios'
import {
  ActivityStageEnum,
  NewYearUnionGameState,
  UnionContestCardInfoResponse,
  UnionContestCardRequestParams,
  UnionContestTaskListResponse,
  UnionContestWidgetInfoResponse,
  UnionContestWarmupState,
} from './types'

import {RootState} from '~/stores'

/**
 * 2025年公会分公会赛任务列表
 * 文档：http://wiki.xunlei.cn/pages/viewpage.action?pageId=174050069
 */
export const get2025YearUnionContestTaskList = createAsyncThunk<
  UnionContestTaskListResponse,
  Record<string, unknown> | undefined,
  {state: RootState}
>(
  'NewYearUnionGame/get2025YearUnionContestTaskList',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2025_year_union_contest_task_list',
      {
        ...params,
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as UnionContestTaskListResponse
    }
    return data as UnionContestTaskListResponse
  }
)
/**
 * 获取2025年公会争霸赛活动预热页信息接口
 * 文档：http://wiki.xunlei.cn/pages/viewpage.action?pageId=174051423
 */
export const get2025YearUnionContestWarmupInfo = createAsyncThunk<
  UnionContestWarmupState,
  Record<string, unknown> | undefined,
  {state: RootState}
>(
  'NewYearUnionGame/get2025YearUnionContestWarmupInfo',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2025_year_union_contest_warmup_info',
      {
        ...params,
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as UnionContestWarmupState
    }
    return data as UnionContestWarmupState
  }
)

/**
 * 2025年公会赛加成卡信息
 * 文档：http://wiki.xunlei.cn/pages/viewpage.action?pageId=174050152
 */
export const get2025YearUnionContestCardInfo = createAsyncThunk<
  UnionContestCardInfoResponse,
  UnionContestCardRequestParams | undefined
>(
  'NewYearUnionGame/get2025YearUnionContestCardInfo',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2025_year_union_contest_card_info',
      {
        ...params,
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as UnionContestCardInfoResponse
    }
    return data as UnionContestCardInfoResponse
  }
)

/**
 * 2025年公会赛组件信息
 * 文档：根据接口文档图片
 * 功能说明：获取2025年公会赛组件所需的信息，包括活动阶段、公会排名、奖励等信息
 */
export const get2025YearUnionContestWidgetInfo = createAsyncThunk<
  UnionContestWidgetInfoResponse,
  Record<string, unknown> | undefined,
  {state: RootState}
>(
  'NewYearUnionGame/get2025YearUnionContestWidgetInfo',
  async (params, store: any) => {
    const resp = await http.post(
      '/live/activity/get_2025_year_union_contest_widget_info',
      {
        ...params,
      },
      {
        store,
        xcErrorToast: true,
      }
    )
    const {data} = resp
    if (data?.data) {
      return data.data as UnionContestWidgetInfoResponse
    }
    return data as UnionContestWidgetInfoResponse
  }
)

const initialState: NewYearUnionGameState = {
  // 任务状态
  task: {
    done: false,
    loading: false,
    groupName: '',
    list: [],
    error: null,
    activity_id: 0,
  },
  // 加成卡状态
  card: {
    loading: false,
    error: null,
    data: {
      is_buff_active: false,
      competition_phase: 0,
      final_contest_start_time: '',
      current_ratio: 0,
      buff_expire_ts: 0,
      user_identity: 0,
      card_list: [],
      new_offset: 0,
      more: false,
      err_msg: '',
      count: 0,
      is_leader_remind_can_use: false,
      is_remind_can_use: false,
    },
  },
  // 组件信息状态
  widget: {
    loading: false,
    error: null,
    data: {
      activity_stage: ActivityStageEnum.hot,
      date_range: [],
      top1_union_info: null,
      my_union_info: null,
      is_anchor: false,
      if_my_union_info: false,
      final_stage_all_groups: [],
      final_stage_group: '',
      final_stage_range: [],
      selection_stage_range: [],
      activity_gift_list: null,
      final_groups_rank_range: null,
      default_final_group: '',
      is_my_union_speeding: false,
      my_union_speed_card_icon: '',
      my_union_speed_expire_ts: 0,
      my_union_speed_odds: null,
      activity_id: null,
    },
  },
  // 预热页信息
  warmup: {} as UnionContestWarmupState,
}

export const NewYearUnionGameSlice = createSlice({
  name: 'NewYearUnionGame',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(get2025YearUnionContestWarmupInfo.fulfilled, (state, action) => {
        Object.assign(state.warmup, action.payload)
      })
      .addCase(get2025YearUnionContestTaskList.pending, (state) => {
        Object.assign(state.task, {
          loading: true,
          error: null,
        })
      })
      .addCase(get2025YearUnionContestTaskList.fulfilled, (state, action) => {
        Object.assign(state.task, {
          loading: false,
          groupName: action.payload.group_name ?? '',
          list: action.payload.tasks ?? [],
          activity_id: action.payload.activity_id,
          done: !state.task.done,
        })
      })
      .addCase(get2025YearUnionContestTaskList.rejected, (state, action) => {
        Object.assign(state.task, {
          loading: false,
          error: action.error.message || '获取任务列表失败',
        })
      })
      .addCase(get2025YearUnionContestCardInfo.pending, (state) => {
        Object.assign(state.card, {
          loading: true,
          error: null,
        })
      })
      .addCase(get2025YearUnionContestCardInfo.fulfilled, (state, action) => {
        Object.assign(state.card, {
          loading: false,
        })
        Object.assign(state.card.data, action.payload)
      })
      .addCase(get2025YearUnionContestCardInfo.rejected, (state, action) => {
        Object.assign(state.card, {
          loading: false,
          error: action.error.message || '获取加成卡信息失败',
        })
      })
      .addCase(get2025YearUnionContestWidgetInfo.pending, (state) => {
        Object.assign(state.widget, {
          loading: true,
          error: null,
        })
      })
      .addCase(get2025YearUnionContestWidgetInfo.fulfilled, (state, action) => {
        Object.assign(state.widget, {
          loading: false,
          data: action.payload,
        })
      })
      .addCase(get2025YearUnionContestWidgetInfo.rejected, (state, action) => {
        Object.assign(state.widget, {
          loading: false,
          error: action.error.message || '获取组件信息失败',
        })
      })
  },
})

export default NewYearUnionGameSlice.reducer
export const NewYearUnionGameActions = NewYearUnionGameSlice.actions
export const useNewYearUnionGameState = () =>
  useSelector((state: RootState) => state.NewYearUnionGame)
